import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const FEEDBACK_WINDOW_DAYS = 21;

export type FeedbackCandidate = {
  ticket_id: string;
  event_id: string;
  event_title: string;
  event_start: string;
  event_image: string | null;
  host_id: string | null;
  secondary_host_id: string | null;
  attendance_status: string | null;
  submitted_at: string | null;
};

type TicketRow = {
  id: string;
  event_id: string;
  attendance_status: string | null;
  events: {
    id: string;
    title: string;
    start_time: string;
    image_url: string | null;
    host_id: string | null;
    secondary_host_id: string | null;
  };
};

export const preferAttendedCandidates = (candidates: FeedbackCandidate[]) => {
  const attended = candidates.filter((candidate) => candidate.attendance_status === "present");
  return attended.length > 0 ? attended : candidates;
};

export const feedbackDismissKey = (ticketId: string) =>
  `feedback-reminder-dismissed-${ticketId}`;

export const useFeedbackCandidates = (userId?: string) =>
  useQuery({
    queryKey: ["feedback-candidates", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<FeedbackCandidate[]> => {
      if (!userId) return [];

      const now = Date.now();
      const cutoffIso = new Date(
        now - FEEDBACK_WINDOW_DAYS * 24 * 60 * 60 * 1000,
      ).toISOString();

      const { data, error } = await supabase
        .from("tickets")
        .select(
          "id, event_id, attendance_status, events!inner(id, title, start_time, image_url, host_id, secondary_host_id)",
        )
        .eq("user_id", userId)
        .eq("status", "confirmed")
        .gte("events.start_time", cutoffIso)
        .lte("events.start_time", new Date(now).toISOString())
        .order("start_time", { foreignTable: "events", ascending: false });

      if (error) throw error;

      const rows = (data ?? []) as unknown as TicketRow[];
      const eventIds = [...new Set(rows.map((row) => row.event_id))];
      const submittedByEvent = new Map<string, string>();

      if (eventIds.length > 0) {
        const { data: submissions, error: submissionsError } = await supabase
          .from("event_feedback")
          .select("event_id, submitted_at")
          .eq("user_id", userId)
          .in("event_id", eventIds);

        if (submissionsError) throw submissionsError;
        submissions?.forEach((submission) => {
          submittedByEvent.set(submission.event_id, submission.submitted_at);
        });
      }

      return preferAttendedCandidates(
        rows.map((row) => ({
          ticket_id: row.id,
          event_id: row.event_id,
          event_title: row.events.title,
          event_start: row.events.start_time,
          event_image: row.events.image_url,
          host_id: row.events.host_id,
          secondary_host_id: row.events.secondary_host_id,
          attendance_status: row.attendance_status,
          submitted_at: submittedByEvent.get(row.event_id) ?? null,
        })),
      );
    },
  });